
import React, { useState } from 'react';
import { UserProfile } from '../types';
import { deriveKeyFromPassword, unwrapKey } from '../utils/crypto';

interface MasterKeyVerifyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  user: UserProfile | null;
  title: string;
  message: string;
  icon?: string;
}

const MasterKeyVerifyDialog: React.FC<MasterKeyVerifyDialogProps> = ({ 
  isOpen, onClose, onVerified, user, title, message, icon = "fa-shield-lock"
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !user?.salt || !user?.encryption_verify) return;

    setLoading(true);
    setError('');

    try {
      const passwordAccessKey = await deriveKeyFromPassword(password, user.salt);
      const masterDataKey = await unwrapKey(user.encryption_verify, passwordAccessKey);
      
      if (masterDataKey) {
        onVerified();
        setPassword('');
        onClose();
      } else {
        setError('Chave Mestra incorreta. Ação bloqueada.');
      }
    } catch (err) {
      setError('Erro ao validar segurança.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-xl animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl border border-red-500/20 animate-in zoom-in-95 duration-300">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl flex items-center justify-center mb-6 text-2xl shadow-inner">
            <i className={`fa-solid ${icon}`}></i>
          </div>
          
          <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 mb-2 leading-tight uppercase tracking-tight">
            {title}
          </h3>
          
          <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-8 leading-relaxed px-2">
            {message}
          </p>
          
          <form onSubmit={handleVerify} className="w-full space-y-4">
            <div className="relative">
              <input 
                required
                autoFocus
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-6 py-5 text-sm font-bold text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-red-500/50 transition-all placeholder:text-slate-400"
                placeholder="Sua Chave Mestra"
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>

            {error && (
              <p className="text-[10px] font-black text-red-600 uppercase tracking-widest animate-pulse">
                {error}
              </p>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <button 
                type="submit"
                disabled={loading || !password}
                className="w-full py-5 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-red-200 dark:shadow-none active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {loading ? <i className="fa-solid fa-circle-notch animate-spin"></i> : "Confirmar com Chave Mestra"}
              </button>
              <button 
                type="button"
                onClick={() => { setPassword(''); setError(''); onClose(); }}
                className="w-full py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MasterKeyVerifyDialog;
